import { useCallback, useEffect, useRef, useState } from "react"
import type { FC, KeyboardEvent } from "react"

import styles from "./MemoCell.module.scss"

import EditIcon from "@/lib/images/EditIcon"
import {
	type FragmentType,
	getFragmentData,
	graphql,
} from "@/souhl_admin/gql/__generated__"

const memoCellFragment = graphql(/* GraphQL */ `
	fragment MemoCell on Salon {
		id
		dataSourcePolicyMemo
	}
`)

const MEMO_MAX_LENGTH = 1000

type MemoCellProps = {
	salon: FragmentType<typeof memoCellFragment>
	onUpdateMemo: (salonId: number, memo: string | null) => Promise<void>
}

const MemoCell: FC<MemoCellProps> = ({
	salon: salonFragment,
	onUpdateMemo,
}) => {
	const { id: salonId, dataSourcePolicyMemo: memo } = getFragmentData(
		memoCellFragment,
		salonFragment
	)
	const [isEditing, setIsEditing] = useState(false)
	const [editValue, setEditValue] = useState(memo ?? "")
	const [saving, setSaving] = useState(false)
	const inputRef = useRef<HTMLTextAreaElement>(null)
	const isCancellingRef = useRef(false)

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus()
			inputRef.current.selectionStart = inputRef.current.value.length
		}
	}, [isEditing])

	const handleStartEdit = useCallback(() => {
		setEditValue(memo ?? "")
		setIsEditing(true)
	}, [memo])

	const handleSave = useCallback(() => {
		if (saving || isCancellingRef.current) return
		setSaving(true)
		const trimmed = editValue.trim()
		onUpdateMemo(salonId, trimmed === "" ? null : trimmed)
			.then(() => {
				setIsEditing(false)
			})
			.catch(() => {
				// エラーは useDatasourceConfig 側で処理済み
			})
			.finally(() => {
				setSaving(false)
			})
	}, [salonId, editValue, onUpdateMemo, saving])

	const handleCancel = useCallback(() => {
		isCancellingRef.current = true
		setIsEditing(false)
		setEditValue(memo ?? "")
		// blur イベントで handleSave が発火するのを防いだ後、フラグをリセット
		requestAnimationFrame(() => {
			isCancellingRef.current = false
		})
	}, [memo])

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Escape") {
				handleCancel()
			}
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault()
				handleSave()
			}
		},
		[handleCancel, handleSave]
	)

	if (isEditing) {
		return (
			<div className={styles.editContainer}>
				<textarea
					ref={inputRef}
					className={styles.input}
					value={editValue}
					onChange={(e) => setEditValue(e.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={handleSave}
					maxLength={MEMO_MAX_LENGTH}
					disabled={saving}
					rows={2}
				/>
				<span className={styles.charCount}>
					{editValue.length}/{MEMO_MAX_LENGTH}
				</span>
			</div>
		)
	}

	return (
		<div className={styles.displayContainer} onClick={handleStartEdit}>
			{memo ? (
				<span className={styles.memoText}>{memo}</span>
			) : (
				<span className={styles.memoEmpty}>メモなし</span>
			)}
			<EditIcon className={styles.editIcon} />
		</div>
	)
}

export default MemoCell
